import SVG from "../../../Components/SVG";
import GradientBlue from "../../../Components/InputFields/GradientBlue";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../config/api";

export default function VerificationModalOff({
  isEmail = false,
  setShowVerificationOff,
  onSuccess,
  setIsVerified,
}) {
  const navigate = useNavigate();
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputNums, setInputNums] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const submitInFlightRef = useRef(false);
  useEffect(() => {
    if (inputNums.every((num) => num !== "")) {
      handleDisable();
    }
  }, [inputNums]);
  const handlePaste = (event, startIndex) => {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData("text") ?? "";
    const digits = pastedText.replace(/[^0-9]/g, "");
    if (!digits) return;
    const maxLength = inputNums.length - startIndex;
    const trimmedDigits = digits.slice(0, maxLength);
    setCodeInvalid(false);
    setInputNums((prev) => {
      const updated = [...prev];
      for (let offset = 0; offset < trimmedDigits.length; offset += 1) {
        updated[startIndex + offset] = trimmedDigits[offset];
        const input = inputRefs.current[startIndex + offset];
        if (input) {
          input.value = trimmedDigits[offset];
        }
      }
      return updated;
    });
    const nextIndex = startIndex + trimmedDigits.length;
    if (nextIndex < inputRefs.current.length) {
      inputRefs.current[nextIndex]?.focus();
    }
  };
  const handleDisable = async (e) => {
    if (e) {
      e.preventDefault();
    }
    if (submitInFlightRef.current) {
      return;
    }
    const verificationCode = inputNums.join("");
    if (verificationCode.length < 6) {
      setCodeInvalid(true);
      return;
    }
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setCodeInvalid(false);
    try {
      let response = await fetch(
        `${API_BASE_URL}auth/2fa/disable/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({ code: verificationCode }),
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(
          `${API_BASE_URL}auth/2fa/disable/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({ code: verificationCode }),
          },
        );
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        const data = await response.json();
        if (data?.detail === "Invalid code.") {
          console.log("code is invalid");
          setCodeInvalid(true);
          return;
        }
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data?.detail === "Invalid code.") {
        console.log("code is invalid");
        setCodeInvalid(true);
        return;
      }
      console.log("Account Data:", data);
      setIsVerified(data.twofa_enabled);
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
    window.location.reload();
  };
  return (
    <div className="w-full bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 gap-7 flex flex-col drop-shadow-lg border border-zi-blue-light">
      <div className="flex flex-col gap-2">
        <div className="w-full flex items-start justify-between gap-3">
          <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
            غیر فعال کردن
          </h1>
          <span onClick={() => setShowVerificationOff(false)}>
            <SVG
              name="close"
              classname="stroke-zi-text-light dark:stroke-zi-text-dark size-5 cursor-pointer"
            />
          </span>
        </div>

        <span className="text-xs font-light text-zi-text-light/50 dark:text-zi-text-dark/50">
          کدی که در Google Authenticator می‌بینید را وارد کنید.
        </span>
      </div>

      <div className="flex flex-col gap-4 items-end">
        <div className="flex justify-between items-center gap-4 [direction:ltr]">
          {Array.from({ length: 6 }).map((_, i) => (
            <input
              key={i}
              ref={(element) => {
                inputRefs.current[i] = element;
              }}
              type="text"
              maxLength="1"
              inputmode="numeric"
              pattern="[0-9]*"
              className="size-full aspect-square rounded-lg caret-zi-blue-light border border-zi-border-light dark:border-zi-border-dark bg-zi-bg2-light dark:bg-zi-bg2-dark hocus:bg-zi-bg1-light hocus:dark:bg-zi-bg1-dark text-center text-xl font-medium text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 hocus:ring-zi-border-light hocus:dark:ring-zi-border-dark [direction:ltr]"
              onInput={(e) => {
                setCodeInvalid(false);
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                setInputNums((prev) => {
                  const newInputNums = [...prev];
                  newInputNums[i] = e.target.value;
                  return newInputNums;
                });
                const next = inputRefs.current[i + 1];
                if (next && e.target.value) next.focus();
              }}
              onPaste={(event) => handlePaste(event, i)}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !e.target.value) {
                  const prev = inputRefs.current[i - 1];
                  if (prev) prev.focus();
                }
                if (e.key === "ArrowLeft") {
                  e.preventDefault();
                  const prev = inputRefs.current[i - 1];
                  if (prev) prev.focus();
                }
                if (e.key === "ArrowRight") {
                  e.preventDefault();
                  const next = inputRefs.current[i + 1];
                  if (next) next.focus();
                }
              }}
            />
          ))}
        </div>
      </div>
      {codeInvalid && (
        <p className="text-red-500 text-sm">کد وارد شده صحیح نمی‌باشد</p>
      )}

      <GradientBlue
        type="submit"
        value="ثبت"
        isLoader={isSubmitting}
        onClick={handleDisable}
      />
    </div>
  );
}
